import { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useConfirmAccountDeletion } from '@/api/hooks/useAccountDeletion';
import { LogoFull } from '@/components/LogoFull';
import { Button } from '@/components/ui/Button';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

function formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
}

export function AccountDeleteConfirmPage() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token') || '';

    const confirmMutation = useConfirmAccountDeletion();
    const [confirmed, setConfirmed] = useState(false);
    const [deletionDate, setDeletionDate] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    async function handleConfirm() {
        if (!token) return;

        setError(null);
        try {
            const result = await confirmMutation.mutateAsync(token);
            setConfirmed(true);
            setDeletionDate(result.deletionDate ?? null);
        } catch (err) {
            if (err instanceof Error) {
                setError(err.message || 'Failed to confirm account deletion.');
            } else {
                setError('An unexpected error occurred.');
            }
        }
    }

    // No token provided
    if (!token) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-gray-50 p-8 dark:bg-dark">
                <div className="w-full max-w-md">
                    <div className="mb-8 text-center">
                        <Link to="/">
                            <LogoFull className="mx-auto h-10 w-auto" />
                        </Link>
                    </div>

                    <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-xs dark:border-gray-700 dark:bg-secondary">
                        <h1 className="mb-6 text-center text-2xl font-bold text-gray-900 dark:text-white">
                            Invalid Link
                        </h1>

                        <div className="mb-6 rounded-md border border-red-200 bg-red-50 p-4 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
                            Invalid confirmation link. Please request account deletion again from your profile settings.
                        </div>

                        <Link to="/profile" className="block">
                            <Button type="button" className="w-full" size="lg">
                                Go to Profile
                            </Button>
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    // Deletion confirmed
    if (confirmed && deletionDate) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-gray-50 p-8 dark:bg-dark">
                <div className="w-full max-w-md">
                    <div className="mb-8 text-center">
                        <Link to="/">
                            <LogoFull className="mx-auto h-10 w-auto" />
                        </Link>
                    </div>

                    <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-xs dark:border-gray-700 dark:bg-secondary">
                        <div className="mb-6 flex justify-center">
                            <div className="rounded-full bg-red-100 p-3 dark:bg-red-900/30">
                                <ExclamationTriangleIcon className="h-8 w-8 text-red-600 dark:text-red-400" />
                            </div>
                        </div>

                        <h1 className="mb-4 text-center text-2xl font-bold text-gray-900 dark:text-white">
                            Account Deletion Confirmed
                        </h1>

                        <div className="mb-6 rounded-md border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
                            <p className="text-center text-red-700 dark:text-red-400">
                                Your account will be permanently deleted on{' '}
                                <span className="font-semibold">{formatDate(deletionDate)}</span>
                            </p>
                        </div>

                        <p className="mb-6 text-center text-sm text-gray-500 dark:text-gray-400">
                            You can cancel this deletion from your profile settings before this date.
                        </p>

                        <Link to="/profile" className="block">
                            <Button type="button" className="w-full" size="lg">
                                Go to Profile
                            </Button>
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    // Confirmation form
    return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50 p-8 dark:bg-dark">
            <div className="w-full max-w-md">
                <div className="mb-8 text-center">
                    <LogoFull className="mx-auto h-10 w-auto" />
                </div>

                <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-xs dark:border-gray-700 dark:bg-secondary">
                    <div className="mb-6 flex justify-center">
                        <div className="rounded-full bg-red-100 p-3 dark:bg-red-900/30">
                            <ExclamationTriangleIcon className="h-8 w-8 text-red-600 dark:text-red-400" />
                        </div>
                    </div>

                    <h1 className="mb-4 text-center text-2xl font-bold text-gray-900 dark:text-white">
                        Confirm Account Deletion
                    </h1>

                    <p className="mb-4 text-center text-gray-600 dark:text-gray-400">
                        Are you sure you want to delete your account? This action will:
                    </p>

                    <ul className="mb-6 list-disc pl-8 text-sm text-gray-600 dark:text-gray-400">
                        <li>Schedule your account for permanent deletion</li>
                        <li>Give you 7 days to cancel</li>
                        <li>After 7 days, all data will be permanently removed</li>
                    </ul>

                    {error && (
                        <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-4 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
                            {error}
                        </div>
                    )}

                    <div className="space-y-3">
                        <Button
                            type="button"
                            variant="danger"
                            className="w-full"
                            size="lg"
                            onClick={handleConfirm}
                            isLoading={confirmMutation.isPending}
                        >
                            Confirm Deletion
                        </Button>

                        <Link to="/profile" className="block">
                            <Button
                                type="button"
                                variant="secondary"
                                className="w-full"
                                size="lg"
                                disabled={confirmMutation.isPending}
                            >
                                Cancel
                            </Button>
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
